import './lib/i18n';
import './styles/globals.css';

import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from '@components/ui/toaster';
import { PageLoading } from '@components/common/LoadingSpinner';

// Admin app
import { AdminLogin } from './apps/admin/AdminLogin';
import { AdminLayout } from './apps/admin/AdminLayout';
import { MagicVerifyPage } from './apps/admin/MagicVerifyPage';
import { useAuth } from '@hooks/useAuth';

function AdminHome() {
  const { hasRole } = useAuth();
  return <Navigate to={hasRole('superadmin') ? '/admin/centers' : '/admin/inicio'} replace />;
}

import { BookingsPage } from './apps/admin/BookingsPage';
import { CalendarPage } from './apps/admin/CalendarPage';
import { InicioPage } from './apps/admin/InicioPage';
import { HorariosPage } from './apps/admin/HorariosPage';
import { GuidePage } from './apps/admin/GuidePage';
import { InformesPage } from './apps/admin/InformesPage';
import { SistemaPage } from './apps/admin/SistemaPage';
import { ServicesPage } from './apps/admin/ServicesPage';
import { StaffPage } from './apps/admin/StaffPage';
import { CustomersPage } from './apps/admin/CustomersPage';
import { HistorialPage } from './apps/admin/HistorialPage';
import { CentersPage } from './apps/admin/CentersPage';
import { SettingsPage } from './apps/admin/SettingsPage';

// Widget
import { BookingWidget } from './apps/widget/BookingWidget';

// Customer portal
import { CustomerPortal } from './apps/customer/CustomerPortal';
import { CustomerConfirm } from './apps/customer/CustomerConfirm';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Do not retry on 4xx errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const router = createBrowserRouter([
  // ---- Admin ----
  {
    path: '/admin/login',
    element: <AdminLogin />,
  },
  {
    path: '/admin/magic-verify',
    element: <MagicVerifyPage />,
  },
  {
    path: '/admin',
    element: <AdminLayout />,
    children: [
      { index: true, element: <AdminHome /> },
      { path: 'inicio', element: <InicioPage /> },
      { path: 'bookings', element: <BookingsPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'services', element: <ServicesPage /> },
      { path: 'staff', element: <StaffPage /> },
      { path: 'customers', element: <CustomersPage /> },
      { path: 'horarios', element: <HorariosPage /> },
      { path: 'informes', element: <InformesPage /> },
      { path: 'historial', element: <HistorialPage /> },
      { path: 'guia', element: <GuidePage /> },
      { path: 'sistema', element: <SistemaPage /> },
      { path: 'centers', element: <CentersPage /> },
      { path: 'settings', element: <SettingsPage /> },
    ],
  },

  // ---- Widget ----
  {
    path: '/widget/:slug',
    element: <BookingWidget />,
  },

  // ---- Email confirmation ----
  {
    path: '/b/confirm/:confirmationToken',
    element: <CustomerConfirm />,
  },

  // ---- Customer portal (magic-link) ----
  {
    path: '/c/:trackingToken',
    element: <CustomerPortal />,
  },

  // ---- Fallback ----
  {
    path: '/',
    element: <Navigate to="/admin" replace />,
  },
  {
    path: '*',
    element: (
      <main className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold">404</h1>
          <p className="text-muted-foreground">Page not found</p>
        </div>
      </main>
    ),
  },
]);

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<PageLoading />}>
        <RouterProvider router={router} />
      </Suspense>
      <Toaster />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);
